import React, { useState, useEffect, useMemo } from "react";
import Context from "../../Context";
import { useToast } from "../../components/ui";
import { getAdminOrderUrl } from "../../constants";
import { Header } from "../../components/modules";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../components/base";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import {
  FiShoppingCart,
  FiUser,
  FiPhone,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
  FiSearch,
  FiTrash2,
  FiLogIn,
  FiExternalLink,
  FiCopy,
  FiRefreshCw,
  FiCheck,
  FiTag,
} from "react-icons/fi";

const PAGE_SIZE = 25;

export const Cart = () => {
  const { callApi, db } = Context();
  const { toast } = useToast();

  const [rawCarts, setRawCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [otpFilter, setOtpFilter] = useState("all"); // 'all' | 'verified' | 'pending'
  const [expireFilter, setExpireFilter] = useState("all"); // 'all' | 'active' | 'expired'
  const [currentPage, setCurrentPage] = useState(1);
  const [now, setNow] = useState(Date.now());
  const [clearingCartId, setClearingCartId] = useState(null);
  const [clearingExpired, setClearingExpired] = useState(false);

  // 1. Live Countdown interval (tick every 1s)
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // 2. Real-time Firestore onSnapshot subscription
  useEffect(() => {
    if (!db) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const q = query(collection(db, "Carts"), where("size", ">", 0));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const cartsList = [];
        snapshot.forEach((doc) => {
          cartsList.push({ id: doc.id, ...doc.data() });
        });
        setRawCarts(cartsList);
        setLoading(false);
      },
      (error) => {
        console.error("Error subscribing to real-time Carts:", error);
        toast({
          className: "bg-rose-600 text-white font-semibold rounded-md",
          title: "เกิดข้อผิดพลาดในการโหลดตะกร้า Real-time",
          description: error.message,
        });
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [db, toast]);

  // 3. Computed Statistics
  const stats = useMemo(() => {
    let totalTickets = 0;
    let verifiedOtpCount = 0;
    let pendingOtpCount = 0;
    let activeCount = 0;
    let expiredCount = 0;

    rawCarts.forEach((cart) => {
      const size = Number(cart.size) || 0;
      totalTickets += size;

      if (cart.otp?.status) {
        verifiedOtpCount++;
      } else {
        pendingOtpCount++;
      }

      if (cart.expireAt && cart.expireAt < now) {
        expiredCount++;
      } else {
        activeCount++;
      }
    });

    return {
      totalCarts: rawCarts.length,
      totalTickets,
      verifiedOtpCount,
      pendingOtpCount,
      activeCount,
      expiredCount,
    };
  }, [rawCarts, now]);

  // 4. Filtering and Searching
  const filteredCarts = useMemo(() => {
    let result = [...rawCarts];

    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      result = result.filter((cart) => {
        const idMatch = cart.id?.toLowerCase().includes(term);
        const phoneMatch = cart.otp?.phone?.includes(term);
        const lotteryMatch =
          cart.lotteryBlocks &&
          Object.keys(cart.lotteryBlocks).some((num) => num.includes(term));
        return idMatch || phoneMatch || lotteryMatch;
      });
    }

    // OTP Status filter
    if (otpFilter === "verified") {
      result = result.filter((cart) => cart.otp?.status === true);
    } else if (otpFilter === "pending") {
      result = result.filter((cart) => !cart.otp?.status);
    }

    // Expire Status filter
    if (expireFilter === "active") {
      result = result.filter((cart) => !cart.expireAt || cart.expireAt >= now);
    } else if (expireFilter === "expired") {
      result = result.filter((cart) => cart.expireAt && cart.expireAt < now);
    }

    // Sort by latest expireAt descending
    result.sort((a, b) => (b.expireAt || 0) - (a.expireAt || 0));

    return result;
  }, [rawCarts, searchTerm, otpFilter, expireFilter, now]);

  // 5. Pagination
  const totalPages = Math.max(1, Math.ceil(filteredCarts.length / PAGE_SIZE));
  const paginatedCarts = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredCarts.slice(start, start + PAGE_SIZE);
  }, [filteredCarts, currentPage]);

  // Handle page reset if totalPages changes
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  // Action: Clear Single Cart
  const handleClearCart = async (cartId) => {
    if (!window.confirm(`คุณแน่ใจหรือไม่ว่าต้องการยกเลิกตะกร้าของลูกค้า #${cartId}?`)) {
      return;
    }

    setClearingCartId(cartId);
    try {
      const res = await callApi(
        "carts-clearCartById",
        { cartRef: cartId },
        { notShowLoading: true }
      );

      if (res) {
        toast({
          className: "bg-emerald-600 text-white font-semibold rounded-md",
          title: `ยกเลิกตะกร้า #${cartId} สำเร็จ`,
        });
      } else {
        toast({
          className: "bg-rose-600 text-white font-semibold rounded-md",
          title: `ไม่สามารถยกเลิกตะกร้า #${cartId} ได้`,
        });
      }
    } catch (err) {
      toast({
        className: "bg-rose-600 text-white font-semibold rounded-md",
        title: "เกิดข้อผิดพลาดในการยกเลิกตะกร้า",
        description: err.message,
      });
    } finally {
      setClearingCartId(null);
    }
  };

  // Action: Clear All Expired Carts
  const handleClearAllExpired = async () => {
    const expiredCarts = rawCarts.filter(
      (c) => c.expireAt && c.expireAt < now
    );

    if (expiredCarts.length === 0) {
      toast({
        title: "ไม่มีตะกร้าที่หมดอายุในขณะนี้",
      });
      return;
    }

    if (
      !window.confirm(
        `ยืนยันการล้างตะกร้าที่หมดอายุแล้วทั้งหมดจำนวน ${expiredCarts.length} ตะกร้า?`
      )
    ) {
      return;
    }

    setClearingExpired(true);
    let successCount = 0;

    for (const cart of expiredCarts) {
      try {
        await callApi(
          "carts-clearCartById",
          { cartRef: cart.id },
          { notShowLoading: true }
        );
        successCount++;
      } catch (e) {
        console.error("Failed to clear cart:", cart.id, e);
      }
    }

    setClearingExpired(false);
    toast({
      className: "bg-emerald-600 text-white font-semibold rounded-md",
      title: `ล้างตะกร้าหมดอายุสำเร็จ ${successCount} จาก ${expiredCarts.length} ตะกร้า`,
    });
  };

  // Action: Login as Customer (Impersonate Admin Token)
  const handleLoginAsCustomer = async (userId) => {
    try {
      toast({
        title: "กำลังสร้างสิทธิ์เข้าสู่ระบบ...",
      });
      const result = await callApi("users-addAdminToken", {
        userId,
      });

      if (result?.adminToken) {
        const targetUrl = getAdminOrderUrl(result.adminToken);
        window.open(targetUrl, "_blank");
      } else {
        toast({
          className: "bg-rose-600 text-white font-semibold rounded-md",
          title: "ไม่สามารถสร้าง Admin Token ได้",
        });
      }
    } catch (error) {
      toast({
        className: "bg-rose-600 text-white font-semibold rounded-md",
        title: "เกิดข้อผิดพลาดในการล็อกอิน",
        description: error.message,
      });
    }
  };

  // Copy to clipboard helper
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      className: "bg-slate-800 text-white font-semibold rounded-md",
      title: `คัดลอก: ${text}`,
    });
  };

  return (
    <div className="w-full max-w-[1600px] mx-auto p-4 sm:p-6 space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 pb-2 border-b border-gray-200">
        <Header
          header="Carts"
          subHeader={
            <div className="flex items-center gap-2">
              <span>จัดการตะกร้าสลาก</span>
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-rose-50 border border-rose-200 rounded-md text-rose-700 text-[11px] font-bold shadow-xs">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500"></span>
                </span>
                <span>REALTIME CONNECTED</span>
              </span>
            </div>
          }
          icon={<FiShoppingCart className="text-amber-500 text-2xl" />}
        />

        <div className="flex items-center gap-3">
          {stats.expiredCount > 0 && (
            <button
              onClick={handleClearAllExpired}
              disabled={clearingExpired}
              className="flex items-center gap-2 bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 px-3.5 py-2 rounded-md text-xs font-bold transition shadow-xs disabled:opacity-50"
              title="ล้างตะกร้าที่หมดอายุแล้วทั้งหมด"
            >
              <FiTrash2 className={clearingExpired ? "animate-spin" : ""} />
              <span>ล้างตะกร้าหมดอายุ ({stats.expiredCount})</span>
            </button>
          )}


        </div>
      </div>

      {/* 4 Summary Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Carts */}
        <div className="bg-white rounded-md p-4 shadow-xs border border-gray-200/80 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500">ตะกร้าที่ใช้งานอยู่</p>
            <h3 className="text-2xl sm:text-3xl font-black text-gray-900 mt-1">
              {stats.totalCarts.toLocaleString()}
              <span className="text-xs font-semibold text-gray-400 ml-1.5">ตะกร้า</span>
            </h3>
            <p className="text-xs text-emerald-600 font-semibold mt-0.5">
              กำลังเลือกซื้อ: {stats.activeCount}
            </p>
          </div>
          <div className="w-12 h-12 rounded-md bg-amber-50 border border-amber-200/60 flex items-center justify-center text-amber-600 text-xl">
            <FiShoppingCart />
          </div>
        </div>

        {/* Total Tickets */}
        <div className="bg-white rounded-md p-4 shadow-xs border border-gray-200/80 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500">สลากในตะกร้ารวม</p>
            <h3 className="text-2xl sm:text-3xl font-black text-amber-600 mt-1">
              {stats.totalTickets.toLocaleString()}
              <span className="text-xs font-semibold text-gray-400 ml-1.5">ใบ</span>
            </h3>
            <p className="text-xs text-gray-500 font-semibold mt-0.5">
              เฉลี่ย {stats.totalCarts > 0 ? (stats.totalTickets / stats.totalCarts).toFixed(1) : 0} ใบ/ตะกร้า
            </p>
          </div>
          <div className="w-12 h-12 rounded-md bg-blue-50 border border-blue-200/60 flex items-center justify-center text-blue-600 text-xl">
            <FiTag />
          </div>
        </div>

        {/* OTP Verified */}
        <div className="bg-white rounded-md p-4 shadow-xs border border-gray-200/80 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500">ยืนยัน OTP แล้ว</p>
            <h3 className="text-2xl sm:text-3xl font-black text-emerald-600 mt-1">
              {stats.verifiedOtpCount.toLocaleString()}
              <span className="text-xs font-semibold text-gray-400 ml-1.5">คน</span>
            </h3>
            <p className="text-xs text-amber-600 font-semibold mt-0.5">
              รอยืนยัน OTP: {stats.pendingOtpCount} คน
            </p>
          </div>
          <div className="w-12 h-12 rounded-md bg-emerald-50 border border-emerald-200/60 flex items-center justify-center text-emerald-600 text-xl">
            <FiCheckCircle />
          </div>
        </div>

        {/* Expired Carts */}
        <div className="bg-white rounded-md p-4 shadow-xs border border-gray-200/80 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-500">หมดอายุแล้ว</p>
            <h3 className="text-2xl sm:text-3xl font-black text-rose-600 mt-1">
              {stats.expiredCount.toLocaleString()}
              <span className="text-xs font-semibold text-gray-400 ml-1.5">ตะกร้า</span>
            </h3>
            <p className="text-xs text-gray-400 font-semibold mt-0.5">
              รอระบบหรือแอดมินล้าง
            </p>
          </div>
          <div className="w-12 h-12 rounded-md bg-rose-50 border border-rose-200/60 flex items-center justify-center text-rose-600 text-xl">
            <FiClock />
          </div>
        </div>
      </div>

      {/* Main Table Container */}
      <div className="bg-white rounded-md shadow-md border border-gray-200 overflow-hidden">
        {/* Table Top Controls (Search & Filter Bar) */}
        <div className="p-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3 bg-gray-50/70">
          {/* Left: Search Input */}
          <div className="relative w-full sm:w-80">
            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="ค้นหารหัสผู้ใช้, เบอร์โทร, หรือเลขสลาก..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-9 pr-3.5 py-2 bg-white border border-gray-300 rounded-md text-xs font-semibold focus:outline-hidden focus:ring-2 focus:ring-amber-400/40 focus:border-amber-500 transition shadow-2xs"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>

          {/* Right: Status Filters & Pagination Summary */}
          <div className="flex flex-wrap items-center gap-2.5">
            {/* OTP Filter */}
            <select
              value={otpFilter}
              onChange={(e) => {
                setOtpFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="bg-white border border-gray-300 rounded-md px-3 py-2 text-xs font-bold text-gray-700 focus:outline-hidden focus:ring-2 focus:ring-amber-400/40"
            >
              <option value="all">OTP ทั้งหมด</option>
              <option value="verified">ยืนยันแล้ว</option>
              <option value="pending">ยังไม่ยืนยัน</option>
            </select>

            {/* Expire Filter */}
            <select
              value={expireFilter}
              onChange={(e) => {
                setExpireFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="bg-white border border-gray-300 rounded-md px-3 py-2 text-xs font-bold text-gray-700 focus:outline-hidden focus:ring-2 focus:ring-amber-400/40"
            >
              <option value="all">สถานะเวลาทั้งหมด</option>
              <option value="active">กำลังเลือกซื้อ</option>
              <option value="expired">หมดอายุแล้ว</option>
            </select>

            {/* Results count badge */}
            <span className="text-xs font-bold bg-amber-100 text-amber-800 px-3 py-1.5 rounded-md border border-amber-200">
              พบ {filteredCarts.length} รายการ
            </span>
          </div>
        </div>

        {/* Live Carts Table */}
        <Table>
          <TableHeader className="bg-slate-100/80 border-b border-gray-200">
            <TableRow>
              <TableHead className="w-12 text-center text-xs font-bold text-gray-600">ลำดับ</TableHead>
              <TableHead className="text-xs font-bold text-gray-600">ข้อมูลลูกค้า (User ID & OTP)</TableHead>
              <TableHead className="text-center text-xs font-bold text-gray-600">จำนวน</TableHead>
              <TableHead className="text-xs font-bold text-gray-600">รายการสลากในตะกร้า (Lottery Blocks)</TableHead>
              <TableHead className="text-center text-xs font-bold text-gray-600">เวลาหมดอายุ / นับถอยหลัง</TableHead>
              <TableHead className="text-center text-xs font-bold text-gray-600">จัดการ (Actions)</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="h-64 text-center">
                  <div className="flex flex-col items-center justify-center gap-2 text-gray-400">
                    <FiRefreshCw className="animate-spin text-3xl text-amber-500" />
                    <p className="text-xs font-bold">กำลังเชื่อมต่อข้อมูลตะกร้าแบบ Real-time...</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredCarts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-64 text-center">
                  <div className="flex flex-col items-center justify-center gap-2 text-gray-400">
                    <FiShoppingCart className="text-4xl text-gray-300" />
                    <p className="text-sm font-bold text-gray-500">ไม่พบข้อมูลตะกร้าสินค้าในขณะนี้</p>
                    <p className="text-xs text-gray-400">
                      {searchTerm ? "ลองเปลี่ยนคำค้นหา หรือรีเซ็ตตัวกรอง" : "ยังไม่มีลูกค้าเลือกใส่สลากในตะกร้า"}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              paginatedCarts.map((cart, idx) => {
                const globalIndex = (currentPage - 1) * PAGE_SIZE + idx + 1;
                return (
                  <CartRow
                    key={cart.id}
                    cart={cart}
                    index={globalIndex}
                    now={now}
                    isClearing={clearingCartId === cart.id}
                    onClearCart={handleClearCart}
                    onLoginAsCustomer={handleLoginAsCustomer}
                    onCopy={copyToClipboard}
                  />
                );
              })
            )}
          </TableBody>
        </Table>

        {/* Pagination Footer */}
        {totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-between p-4 border-t border-gray-200 bg-gray-50/80">
            <p className="text-xs font-semibold text-gray-500">
              แสดงหน้า <strong className="text-gray-900">{currentPage}</strong> จากทั้งหมด{" "}
              <strong className="text-gray-900">{totalPages}</strong> หน้า (รวม {filteredCarts.length} ตะกร้า)
            </p>

            <Pagination className="w-auto m-0">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    className={`${currentPage > 1
                      ? "cursor-pointer hover:bg-white"
                      : "cursor-not-allowed opacity-40 hover:bg-transparent"
                      } font-bold text-xs rounded-md`}
                    onClick={() => currentPage > 1 && setCurrentPage((p) => p - 1)}
                  />
                </PaginationItem>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => Math.abs(p - currentPage) <= 2 || p === 1 || p === totalPages)
                  .map((p) => (
                    <PaginationItem key={p}>
                      <PaginationLink
                        className={`${currentPage === p
                          ? "bg-amber-500 text-white hover:bg-amber-600 font-bold"
                          : "hover:bg-white text-gray-700"
                          } rounded-md cursor-pointer text-xs`}
                        onClick={() => setCurrentPage(p)}
                      >
                        {p}
                      </PaginationLink>
                    </PaginationItem>
                  ))}

                <PaginationItem>
                  <PaginationNext
                    className={`${currentPage < totalPages
                      ? "cursor-pointer hover:bg-white"
                      : "cursor-not-allowed opacity-40 hover:bg-transparent"
                      } font-bold text-xs rounded-md`}
                    onClick={() => currentPage < totalPages && setCurrentPage((p) => p + 1)}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════
// 🛒 Subcomponent: CartRow
// ══════════════════════════════════════════════════════════════════
const CartRow = ({
  cart,
  index,
  now,
  isClearing,
  onClearCart,
  onLoginAsCustomer,
  onCopy,
}) => {
  const [copied, setCopied] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);

  // Expiry countdown calculations
  const expireTimestamp = cart.expireAt || 0;
  const isExpired = expireTimestamp < now;
  const diffMs = Math.max(0, expireTimestamp - now);
  const totalSeconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  const handleCopyId = () => {
    onCopy(cart.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleLogin = async () => {
    setLoggingIn(true);
    await onLoginAsCustomer(cart.id);
    setLoggingIn(false);
  };

  return (
    <TableRow className={`hover:bg-amber-50/30 transition-colors ${isExpired ? "bg-rose-50/20" : ""}`}>
      {/* Index */}
      <TableCell className="text-center font-bold text-xs text-gray-500 align-top py-3.5">
        {index}
      </TableCell>

      {/* Customer Info (User ID, Phone, OTP) */}
      <TableCell className="align-top py-3.5">
        <div className="space-y-1.5">
          {/* User ID */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.open(`/user/${cart.id}`, "_blank")}
              className="group flex items-center gap-1.5 font-mono font-bold text-xs bg-slate-100 hover:bg-slate-200 text-slate-800 px-2.5 py-1 rounded-md border border-slate-300/80 transition"
              title="เปิดหน้าโปรไฟล์ลูกค้า"
            >
              <FiUser className="text-slate-500 text-xs" />
              <span>{cart.id}</span>
              <FiExternalLink className="text-[10px] text-slate-400 group-hover:text-slate-700" />
            </button>

            <button
              onClick={handleCopyId}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-md transition"
              title="คัดลอก User ID"
            >
              {copied ? <FiCheck className="text-emerald-500 text-xs" /> : <FiCopy className="text-xs" />}
            </button>
          </div>

          {/* OTP & Phone Block */}
          {cart.otp?.phone ? (
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              <span className="flex items-center gap-1 font-mono font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded-md border border-gray-200">
                <FiPhone className="text-gray-400 text-[10px]" />
                {cart.otp.phone}
              </span>

              {cart.otp.status ? (
                <span className="flex items-center gap-1 font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-md border border-emerald-200">
                  <FiCheckCircle className="text-[10px]" />
                  ยืนยัน OTP แล้ว
                </span>
              ) : (
                <span className="flex items-center gap-1 font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-md border border-amber-200">
                  <FiAlertCircle className="text-[10px]" />
                  รอยืนยัน OTP
                </span>
              )}

              {cart.otp.code && (
                <span className="font-mono text-[11px] text-gray-600 bg-slate-50 px-1.5 py-0.5 rounded-md border border-gray-200">
                  OTP: <strong>{cart.otp.code}</strong> (Ref: {cart.otp.ref || "-"})
                </span>
              )}
            </div>
          ) : (
            <span className="text-[11px] font-semibold text-gray-400 bg-gray-50 px-2 py-0.5 rounded-md border border-dashed border-gray-200">
              ไม่มีข้อมูล OTP
            </span>
          )}
        </div>
      </TableCell>

      {/* Ticket Size Badge */}
      <TableCell className="text-center align-top py-3.5">
        <span className="inline-flex items-center justify-center font-black text-xs bg-amber-500 text-white px-2.5 py-1 rounded-md shadow-2xs">
          {cart.size} ใบ
        </span>
      </TableCell>

      {/* Lottery Blocks */}
      <TableCell className="align-top py-3.5">
        {cart.lotteryBlocks && Object.keys(cart.lotteryBlocks).length > 0 ? (
          <div className="flex flex-wrap gap-1.5 max-w-md">
            {Object.entries(cart.lotteryBlocks).map(([num, tickets]) => (
              <div
                key={num}
                className="flex items-center gap-1 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/80 rounded-md px-2 py-1 shadow-2xs"
              >
                <span className="font-mono font-black text-xs text-blue-900 tracking-wider">
                  {num}
                </span>
                <span className="text-[10px] font-bold bg-blue-600 text-white rounded-md px-1.5 py-0.2">
                  ×{Array.isArray(tickets) ? tickets.length : 1}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <span className="text-xs text-gray-400 font-semibold">- ไม่มีสลาก -</span>
        )}
      </TableCell>

      {/* Expiry Time & Live Countdown */}
      <TableCell className="text-center align-top py-3.5">
        {expireTimestamp ? (
          <div className="space-y-1">
            <div className="text-xs font-semibold text-gray-600">
              {new Date(expireTimestamp).toLocaleDateString("th-TH", {
                day: "numeric",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })} น.
            </div>

            {isExpired ? (
              <div className="inline-flex items-center gap-1 bg-rose-100 text-rose-700 border border-rose-200 px-2 py-0.5 rounded-md text-xs font-black">
                <FiClock className="text-xs" />
                <span>หมดอายุแล้ว</span>
              </div>
            ) : (
              <div
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-black font-mono shadow-2xs ${minutes < 5
                  ? "bg-amber-100 text-amber-800 border border-amber-300 animate-pulse"
                  : "bg-emerald-100 text-emerald-800 border border-emerald-300"
                  }`}
              >
                <FiClock className="text-xs" />
                <span>
                  เหลือ {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")} นาที
                </span>
              </div>
            )}
          </div>
        ) : (
          <span className="text-xs text-gray-400 font-semibold">-</span>
        )}
      </TableCell>

      {/* Actions */}
      <TableCell className="text-center align-top py-3.5">
        <div className="flex items-center justify-center gap-2">
          {/* Login as User */}
          <button
            onClick={handleLogin}
            disabled={loggingIn}
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white px-3 py-1.5 rounded-md text-xs font-bold transition shadow-xs disabled:opacity-50"
            title="เข้าสู่ระบบเป็นลูกค้า (Login as User)"
          >
            <FiLogIn className={loggingIn ? "animate-spin" : ""} />
            <span>ล็อกอินลูกค้า</span>
          </button>

          {/* Clear Cart */}
          <button
            onClick={() => onClearCart(cart.id)}
            disabled={isClearing}
            className="flex items-center justify-center p-2 rounded-md bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 transition shadow-2xs active:scale-95 disabled:opacity-50"
            title="ยกเลิกตะกร้านี้"
          >
            <FiTrash2 className={`text-sm ${isClearing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </TableCell>
    </TableRow>
  );
};

export default Cart;
